/**
 * MIPTech Widget Constants
 * Application-wide constants for the enterprise chatbot widget
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: 'https://api.miptech.com/v1',
  WEBSOCKET_URL: 'wss://ws.miptech.com',
  CDN_URL: 'https://cdn.miptech.com',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  BATCH_SIZE: 50,
  MAX_MESSAGE_LENGTH: 4000,
  MAX_FILE_SIZE: 10485760, // 10MB
  SUPPORTED_FILE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain']
};

// Performance Thresholds
export const PERFORMANCE = {
  MAX_BUNDLE_SIZE: 153600, // 150KB
  MAX_LOAD_TIME: 2000, // 2 seconds
  MAX_MEMORY_USAGE: 10485760, // 10MB
  MAX_MESSAGE_HISTORY: 100,
  DEBOUNCE_DELAY: 250,
  ANIMATION_DURATION: 300,
  LAZY_LOAD_THRESHOLD: 3000
};

// Security Configuration
export const SECURITY = {
  CSP_NONCE_LENGTH: 32,
  CSRF_TOKEN_LENGTH: 64,
  ENCRYPTION_KEY_LENGTH: 256,
  SESSION_TIMEOUT: 3600000, // 1 hour
  RATE_LIMIT_REQUESTS: 100,
  RATE_LIMIT_WINDOW: 60000, // 1 minute
  XSS_ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br', 'code', 'pre'],
  XSS_ALLOWED_ATTRS: ['href', 'target', 'rel']
};

// Widget States
export const WIDGET_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
  DESTROYED: 'destroyed'
};

// Connection States (used by chat-window for WebSocket status display)
export const CONNECTION_STATES = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

// Message Types
export const MESSAGE_TYPES = {
  USER: 'user',
  BOT: 'bot',
  SYSTEM: 'system',
  ERROR: 'error',
  TYPING: 'typing',
  FILE: 'file',
  PRODUCT: 'product',
  QUICK_REPLY: 'quick_reply'
};

// Event Types
export const EVENT_TYPES = {
  WIDGET_READY: 'widget:ready',
  WIDGET_OPENED: 'widget:opened',
  WIDGET_CLOSED: 'widget:closed',
  WIDGET_MINIMIZED: 'widget:minimized',
  WIDGET_MAXIMIZED: 'widget:maximized',
  MESSAGE_SENT: 'message:sent',
  MESSAGE_RECEIVED: 'message:received',
  CONNECTION_ESTABLISHED: 'connection:established',
  CONNECTION_LOST: 'connection:lost',
  ERROR_OCCURRED: 'error:occurred',
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  FILE_UPLOADED: 'file:uploaded',
  CONVERSATION_STARTED: 'conversation:started',
  CONVERSATION_ENDED: 'conversation:ended'
};

// Platform Types
export const PLATFORMS = {
  WORDPRESS: 'wordpress',
  SHOPIFY: 'shopify',
  WOOCOMMERCE: 'woocommerce',
  MAGENTO: 'magento',
  OPENCART: 'opencart',
  PRESTASHOP: 'prestashop',
  CUSTOM: 'custom',
  UNKNOWN: 'unknown'
};

// Themes
export const THEMES = {
  DEFAULT: 'default',
  MINIMAL: 'minimal',
  MODERN: 'modern',
  DARK: 'dark',
  LIGHT: 'light',
  CUSTOM: 'custom'
};

// Languages
export const LANGUAGES = {
  EN: 'en',
  IT: 'it',
  ES: 'es',
  FR: 'fr',
  DE: 'de',
  PT: 'pt',
  RU: 'ru',
  AR: 'ar',
  ZH: 'zh',
  JA: 'ja'
};

// RTL Languages
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Device Types
export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

// Breakpoints (Bootstrap-like)
export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1400
};

// Animation Types
export const ANIMATIONS = {
  SLIDE_UP: 'slideUp',
  SLIDE_DOWN: 'slideDown',
  SLIDE_LEFT: 'slideLeft',
  SLIDE_RIGHT: 'slideRight',
  FADE_IN: 'fadeIn',
  FADE_OUT: 'fadeOut',
  SCALE_IN: 'scaleIn',
  SCALE_OUT: 'scaleOut',
  BOUNCE: 'bounce',
  SHAKE: 'shake'
};

// Widget Positions
export const POSITIONS = {
  BOTTOM_RIGHT: 'bottom-right',
  BOTTOM_LEFT: 'bottom-left',
  TOP_RIGHT: 'top-right',
  TOP_LEFT: 'top-left',
  CENTER: 'center',
  CUSTOM: 'custom'
};

// Storage Keys
export const STORAGE_KEYS = {
  WIDGET_STATE: 'miptech_widget_state',
  SESSION_ID: 'miptech_session_id',
  USER_PREFERENCES: 'miptech_user_preferences',
  CONVERSATION_HISTORY: 'miptech_conversation_history',
  GDPR_CONSENT: 'miptech_gdpr_consent',
  THEME_PREFERENCE: 'miptech_theme_preference',
  LANGUAGE_PREFERENCE: 'miptech_language_preference'
};

// Error Codes
export const ERROR_CODES = {
  INITIALIZATION_FAILED: 'INIT_001',
  INVALID_CONFIGURATION: 'CONFIG_001',
  API_UNAVAILABLE: 'API_001',
  WEBSOCKET_CONNECTION_FAILED: 'WS_001',
  AUTHENTICATION_FAILED: 'AUTH_001',
  RATE_LIMIT_EXCEEDED: 'RATE_001',
  MESSAGE_TOO_LONG: 'MSG_001',
  FILE_TOO_LARGE: 'FILE_001',
  UNSUPPORTED_FILE_TYPE: 'FILE_002',
  NETWORK_ERROR: 'NET_001',
  STORAGE_UNAVAILABLE: 'STORAGE_001',
  GDPR_CONSENT_REQUIRED: 'GDPR_001',
  PLATFORM_NOT_SUPPORTED: 'PLATFORM_001'
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};

// WebSocket Close Codes
export const WS_CLOSE_CODES = {
  NORMAL_CLOSURE: 1000,
  GOING_AWAY: 1001,
  PROTOCOL_ERROR: 1002,
  UNSUPPORTED_DATA: 1003,
  NO_STATUS_RECEIVED: 1005,
  ABNORMAL_CLOSURE: 1006,
  INVALID_FRAME_PAYLOAD_DATA: 1007,
  POLICY_VIOLATION: 1008,
  MESSAGE_TOO_BIG: 1009,
  MANDATORY_EXTENSION: 1010,
  INTERNAL_ERROR: 1011,
  SERVICE_RESTART: 1012,
  TRY_AGAIN_LATER: 1013,
  TLS_HANDSHAKE: 1015
};

// Regular Expressions
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  API_KEY: /^[a-zA-Z0-9_-]{32,128}$/,
  SESSION_ID: /^[a-zA-Z0-9_-]{16,64}$/,
  TENANT_ID: /^[a-zA-Z0-9_-]{8,32}$/,
  HTML_TAGS: /<[^>]*>/g,
  SCRIPT_TAGS: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  STYLE_TAGS: /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi
};

// Default Widget Dimensions
export const DIMENSIONS = {
  TRIGGER_SIZE: 56,
  WIDGET_WIDTH: 380,
  WIDGET_HEIGHT: 600,
  WIDGET_MIN_WIDTH: 320,
  WIDGET_MIN_HEIGHT: 400,
  WIDGET_MAX_WIDTH: 500,
  WIDGET_MAX_HEIGHT: 800,
  MOBILE_THRESHOLD: 768
};

// Accessibility
export const A11Y = {
  FOCUS_TRAP_ENABLED: true,
  ARIA_LIVE_REGION: 'polite',
  MIN_CONTRAST_RATIO: 4.5,
  MIN_TOUCH_TARGET: 44,
  KEYBOARD_NAVIGATION: true,
  SCREEN_READER_SUPPORT: true
};

// GDPR Compliance
export const GDPR = {
  CONSENT_REQUIRED: true,
  DATA_RETENTION_DAYS: 365,
  EXPORT_FORMATS: ['json', 'csv', 'xml'],
  ANONYMIZATION_ENABLED: true,
  COOKIE_CONSENT_REQUIRED: true
};

// Analytics Events
export const ANALYTICS_EVENTS = {
  WIDGET_LOADED: 'widget_loaded',
  WIDGET_OPENED: 'widget_opened',
  WIDGET_CLOSED: 'widget_closed',
  MESSAGE_SENT: 'message_sent',
  MESSAGE_RECEIVED: 'message_received',
  FILE_UPLOADED: 'file_uploaded',
  ERROR_OCCURRED: 'error_occurred',
  CONVERSATION_STARTED: 'conversation_started',
  CONVERSATION_ENDED: 'conversation_ended',
  FEEDBACK_SUBMITTED: 'feedback_submitted'
};

// Feature Flags (for A/B testing)
export const FEATURE_FLAGS = {
  VOICE_INPUT: 'voice_input',
  FILE_UPLOAD: 'file_upload',
  PRODUCT_RECOMMENDATIONS: 'product_recommendations',
  TYPING_INDICATORS: 'typing_indicators',
  READ_RECEIPTS: 'read_receipts',
  EMOJI_REACTIONS: 'emoji_reactions',
  CONVERSATION_HISTORY: 'conversation_history',
  GDPR_CONTROLS: 'gdpr_controls'
};

// Widget Build Information
export const BUILD_INFO = {
  VERSION: '1.0.0',
  BUILD_DATE: new Date().toISOString(),
  ENVIRONMENT: process.env.NODE_ENV || 'development',
  API_VERSION: 'v1'
};

// Browser Support
export const BROWSER_SUPPORT = {
  CHROME: 60,
  FIREFOX: 55,
  SAFARI: 12,
  EDGE: 79,
  OPERA: 47,
  SAMSUNG: 7.2,
  IE: false // Not supported
};

export default {
  API_CONFIG,
  PERFORMANCE,
  SECURITY,
  WIDGET_STATES,
  MESSAGE_TYPES,
  EVENT_TYPES,
  PLATFORMS,
  THEMES,
  LANGUAGES,
  RTL_LANGUAGES,
  DEVICE_TYPES,
  BREAKPOINTS,
  ANIMATIONS,
  POSITIONS,
  STORAGE_KEYS,
  ERROR_CODES,
  HTTP_STATUS,
  WS_CLOSE_CODES,
  REGEX,
  DIMENSIONS,
  A11Y,
  GDPR,
  ANALYTICS_EVENTS,
  FEATURE_FLAGS,
  BUILD_INFO,
  BROWSER_SUPPORT
};